import React, {
    PropsWithChildren,
    useCallback,
    useState,
    ClipboardEvent,
    useEffect,
    SetStateAction,
    Dispatch,
    forwardRef,
    useImperativeHandle,
    useRef,
} from 'react';
import useActiveElement from '../../../hooks/useActiveElement';
import { InputDropdown, InputMasking } from 'component-library/index';
import { callAcquisitionBff } from 'utils/acquisition-api/AcquisitionBff';
import { FUNDING_ERROR_REGISTRY, IFundingProps, useFundingModuleContext } from '../FundingModule';
import { Row, Col } from '../../../importCL';
import { UUIDv4 } from '../../../utils/cob-utils';
import { generateErrorMessage } from '../../../utils/error-message';
import { isNewErrorMessagingFlagActive } from '../../../utils/error-message-flag';

type TBankRoutingVerificationResponse = boolean;

interface IBankDetailValidity {
    isRoutingNumInError: boolean;
    setIsRoutingNumInError: Dispatch<SetStateAction<boolean>>;
    isAccountNumInError: boolean;
    setIsAccountNumInError:  Dispatch<SetStateAction<boolean>>;
    isVerifyNumInError: boolean;
    setIsVerifyNumInError: Dispatch<SetStateAction<boolean>>;
    doAccountNumbersMatch: boolean;
    setDoAccountNumbersMatch: Dispatch<SetStateAction<boolean>>;
}

export interface BankDetailProps extends IFundingProps, IBankDetailValidity {
    inputRoutingNumber: string;
    inputAccountNumber: string;
    inputVerifyAccountNumber: string;
    setInputRoutingNumber: (inputRoutingNumber: string) => void;
    setInputAccountNumber: (inputAccountNumber: string) => void;
    setInputVerifyAccountNumber: (inputVerifyAccountNumber: string) => void;
    routingNumberEl: React.MutableRefObject<HTMLInputElement>;
    accountNumberEl: React.MutableRefObject<HTMLInputElement>; 
    reAccountNumberEl: React.MutableRefObject<HTMLInputElement>;
    singlePageFunding?: boolean;
}

const BankDetailInput = forwardRef((props: PropsWithChildren<BankDetailProps>, ref) => {
    const { bankDetails, setBankDetails } = useFundingModuleContext();
    const [ inputAccountType, setInputAccountType ] = useState('Checking');
    const [ inputRoutingNumber, setInputRoutingNumber ] = useState('');
    const [ inputAccountNumber, setInputAccountNumber ] = useState('');
    const [ inputVerifyAccountNumber, setInputVerifyAccountNumber ] = useState('');
    const routingValidationValue = useRef<string | null>(null);

    const routingError = isNewErrorMessagingFlagActive
        ? generateErrorMessage('Routing Number', inputRoutingNumber ? undefined : 'missingValue')
        : props.routingError || '';

    const [validatedRoutingNumbers, setValidatedRoutingNumbers] = useState<{ [name in string]: 'Pending' | 'Valid' | 'Invalid' }>({});

    const activeElement = useActiveElement();


    const onPasteEvent = useCallback((regex: RegExp) => (e: ClipboardEvent) => {
        e.preventDefault();
        const sanitizedData = e.clipboardData.getData('text').replace(regex, '');
        document.execCommand('insertText', false, sanitizedData);
    }, []);

    // DPM-13219
    useImperativeHandle(ref, () => ({
        validate: (): Array<FUNDING_ERROR_REGISTRY> => {
            const errors: Array<FUNDING_ERROR_REGISTRY> = []
            if (
                inputRoutingNumber.length < 9 ||
                props.isRoutingNumInError ||
                validatedRoutingNumbers[inputRoutingNumber] !== 'Valid'
            ) {
                errors.push(isNewErrorMessagingFlagActive && !inputRoutingNumber ? FUNDING_ERROR_REGISTRY.ROUTING_NUMBER_EMPTY : FUNDING_ERROR_REGISTRY.ROUTING_NUMBER);
            }
            if (inputAccountNumber.length < 4 || props.isAccountNumInError) {
                errors.push(isNewErrorMessagingFlagActive ? FUNDING_ERROR_REGISTRY.ACCOUNT_NUMBER_EMPTY : FUNDING_ERROR_REGISTRY.ACCOUNT_NUMBER);
            }
            if (inputVerifyAccountNumber.length < 4 || props.isVerifyNumInError) {
                errors.push(isNewErrorMessagingFlagActive ? FUNDING_ERROR_REGISTRY.ACCOUNT_NUMBER_RE_REQUIRED : FUNDING_ERROR_REGISTRY.ACCOUNT_NUMBER_RE);
            } else if (!props.doAccountNumbersMatch || inputAccountNumber !== inputVerifyAccountNumber) {
                errors.push(FUNDING_ERROR_REGISTRY.ACCOUNT_NO_MATCH);
            }

            return errors;
        },
    }));

    useEffect(() => {

        const clearBankFields = () => {
            if(!props.singlePageFunding) {
                return;
            }
           setBankDetails({
                valid: false,
                details: {
                    AccountType: '',
                    AccountNumber: '',
                    RoutingNumber: '',
                },
            });
        };

        if (bankDetails.valid) {
            setInputAccountType(bankDetails.details.AccountType);
            setInputAccountNumber(bankDetails.details.AccountNumber);
            setInputVerifyAccountNumber(bankDetails.details.AccountNumber);
            setInputRoutingNumber(bankDetails.details.RoutingNumber);
            setValidatedRoutingNumbers((prev) => ({ ...prev, [bankDetails.details.RoutingNumber]: 'Valid' }));
        }

        return () => {
            clearBankFields();
        }
    }, []);

    useEffect(() => {
        const validateFields = (): boolean => {
            if (
                inputRoutingNumber.length < 9 ||
                props.isRoutingNumInError ||
                validatedRoutingNumbers[inputRoutingNumber] !== 'Valid' ||
                inputAccountNumber.length < 4 ||
                props.isAccountNumInError ||
                !props.doAccountNumbersMatch ||
                inputAccountNumber !== inputVerifyAccountNumber
            ) {
                return false;
            }
            return true;
        };
        if (validateFields()) {
            setBankDetails({
                valid: true,
                details: {
                    AccountType: inputAccountType,
                    AccountNumber: inputAccountNumber,
                    RoutingNumber: inputRoutingNumber,
                },
            });
        } else {
            setBankDetails({
                valid: false,
            });
        }
    }, [
        inputAccountType,
        inputRoutingNumber,
        inputAccountNumber,
        inputVerifyAccountNumber,
        props.isRoutingNumInError,
        validatedRoutingNumbers,
        props.isAccountNumInError,
        props.isVerifyNumInError,
        props.doAccountNumbersMatch,
    ]);

    const handleSetValue = useCallback((nextValue: string): void => {
        if (nextValue === inputRoutingNumber) {
            return;
        }
        routingValidationValue.current = null;
        props.setIsRoutingNumInError(false);
        setInputRoutingNumber(nextValue);
    }, [
        inputRoutingNumber,
        setInputRoutingNumber,
        props.setIsRoutingNumInError,
    ]);

    const checkAccReAccMatch = useCallback((firstAccountNumber: string, secondAccountNumber: string) => {
        if (firstAccountNumber.trim() === '' || secondAccountNumber.trim() === '') {
            return props.setDoAccountNumbersMatch(true);
        } 
        props.setDoAccountNumbersMatch(firstAccountNumber === secondAccountNumber);
    }, []);

    const checkRoutingVerificationEP = useCallback(async (routingNumber: string): Promise<void> => {
        routingValidationValue.current = routingNumber;
        props.setIsRoutingNumInError(false);
        
        if (routingNumber.length !== 9) {
            props.setIsRoutingNumInError(true);
            return;
        }

        if (validatedRoutingNumbers[routingNumber]) {
            props.setIsRoutingNumInError(validatedRoutingNumbers[routingNumber] === 'Invalid');
            return;
        }
        setValidatedRoutingNumbers((prev) => (prev[routingNumber] = 'Pending') && prev);
        try {
            if(props.apiVersion === 'new') {
                const response = await callAcquisitionBff<TBankRoutingVerificationResponse>('routing-verification', { append: `/${routingNumber}`, headers: {"correlationId" : UUIDv4()} });
                if (routingValidationValue.current === routingNumber) {
                    props.setIsRoutingNumInError(!response);
                }
                setValidatedRoutingNumbers((prev) => ({ ...prev, [routingNumber]: response ? 'Valid' : 'Invalid' }));
            } else {
                const response = await callAcquisitionBff<TBankRoutingVerificationResponse>('routing-verification', { append: `/${routingNumber}` });
                if (routingValidationValue.current === routingNumber) {
                    props.setIsRoutingNumInError(!response);
                }
                setValidatedRoutingNumbers((prev) => ({ ...prev, [routingNumber]: response ? 'Valid' : 'Invalid' }));
            }
        } catch (error) {
            if (routingValidationValue.current === routingNumber) {
                props.setIsRoutingNumInError(true);
            }
            setValidatedRoutingNumbers((prev) => ({ ...prev, [routingNumber]: 'Invalid' }));
            console.error('Routing number verification failed');
        }
    }, [
        validatedRoutingNumbers,
        setValidatedRoutingNumbers,
        props.setIsRoutingNumInError,
    ]);

    const getReenterErrorText = useCallback((): string => {
        if (inputVerifyAccountNumber.length === 0) {
            return props.accountError || generateErrorMessage('Account Number', 'missingValue');
        }
        if (inputVerifyAccountNumber.length < 4) {
            return props.accountError || generateErrorMessage('Account Number');
        }
        return props.reenterError || 'Account Numbers should match';
    }, [props, inputVerifyAccountNumber]);

    return (
        <Row>
            <Col sm={12} md={7} lg={7} className='FundingModule__bankDetailInput px-12 py-4 md:px-6'>
                <InputDropdown
                    id='accountType'
                    name='accountType'
                    label='Account Type'
                    options={['Checking', 'Savings']}
                    value={inputAccountType}
                    setValue={setInputAccountType}
                    useSuccessIcon
                />
                <div id='routingNumber-field-container'>
                    <InputMasking
                        ref={props.routingNumberEl}
                        className='FundingModule__bankInput'
                        alternate
                        label='Routing Number'
                        name='routingNumber'
                        value={inputRoutingNumber}
                        setValue={(nextValue) => handleSetValue(nextValue)}
                        onBlur={() => checkRoutingVerificationEP(inputRoutingNumber)}
                        requiredMessage={routingError}
                        invalidErrorMessage={routingError}
                        minLength={9}
                        required
                        externalValidation
                        error={!!(props.isRoutingNumInError)}
                        errorText={routingError}
                        autoComplete='off'
                        showExclamationError
                        removeAsterisk
                        maskingOptions='routing'
                        useSuccessIcon
                        showSuccessIcon={
                            activeElement !== props.routingNumberEl.current &&
                            !props.isRoutingNumInError && 
                            validatedRoutingNumbers[inputRoutingNumber] === 'Valid' &&
                            inputRoutingNumber && 
                            inputRoutingNumber.length === 9
                        }
                    />
                </div>
                <div id='accountNumber-field-container'>
                    <InputMasking
                        ref={props.accountNumberEl}
                        className='FundingModule__bankInput'
                        alternate
                        label='Account Number'
                        name='accountNumber'
                        value={inputAccountNumber}
                        setValue={(value: string): void => {
                            if (value === inputAccountNumber) {
                                return;
                            }
                            props.setIsAccountNumInError(false);
                            props.setDoAccountNumbersMatch(true);
                            setInputAccountNumber(value);
                        }}
                        onPaste={onPasteEvent(/[^a-zA-Z0-9]/g)}
                        onBlur={() => {
                            checkAccReAccMatch(inputAccountNumber, inputVerifyAccountNumber);
                            props.setIsAccountNumInError(inputAccountNumber.length < 4);
                        }}
                        data-testid='acc-number'
                        minLength={4}
                        externalValidation
                        requiredMessage={props.accountError || ''}
                        invalidErrorMessage={props.accountError || ''}
                        errorText={props.accountError || ''}
                        error={props.isAccountNumInError}
                        autoComplete='off'
                        showExclamationError
                        maskingOptions='account'
                        useSuccessIcon
                        showSuccessIcon={
                            activeElement !== props.accountNumberEl.current && 
                            inputAccountNumber && 
                            !props.isAccountNumInError
                        }
                    />
                </div>
                <div id='reAccountNumber-field-container'>
                    <InputMasking
                        ref={props.reAccountNumberEl}
                        className='FundingModule__bankInput'
                        alternate
                        label='Re-Enter Account Number'
                        name='reEnterAccountNumber'
                        value={inputVerifyAccountNumber}
                        setValue={(value: string): void => {
                            if (value === inputVerifyAccountNumber) {
                                return;
                            }
                            props.setIsVerifyNumInError(false);
                            props.setDoAccountNumbersMatch(true);
                            setInputVerifyAccountNumber(value);
                        }}
                        onBlur={() => {
                            checkAccReAccMatch(inputAccountNumber, inputVerifyAccountNumber);
                            props.setIsVerifyNumInError(inputVerifyAccountNumber.length < 4);
                        }}
                        onPaste={onPasteEvent(/[^a-zA-Z0-9]/g)}
                        minLength={4}
                        externalValidation
                        error={props.isVerifyNumInError || !props.doAccountNumbersMatch}
                        autoComplete='off'
                        requiredMessage={props.accountError || ''}
                        invalidErrorMessage={props.accountError || ''}
                        errorText={
                            getReenterErrorText()
                        }
                        showExclamationError
                        maskingOptions='account'
                        useSuccessIcon
                        showSuccessIcon={
                            activeElement !== props.reAccountNumberEl.current && 
                            props.doAccountNumbersMatch && 
                            inputAccountNumber === inputVerifyAccountNumber &&
                            inputVerifyAccountNumber && 
                            inputVerifyAccountNumber.length >= 4
                        }
                    />
                </div>
            </Col>
            <Col xs={12} md={5} lg={5} className='FundingModule__sampleCheckName'>
                {props.sampleCheckPath && (
                    <img
                        width='300px'
                        loading='lazy'
                        className='my-80-medium my-15-mobile'
                        src={props.sampleCheckPath}
                        alt='Routing Number diagram'
                    />
                )}
            </Col>
        </Row>
    );
});

export default BankDetailInput;
